import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useLenis from "@/lib/useLenis";
import useSEO, { useJsonLd } from "@/lib/useSEO";
import useContent from "@/lib/useContent";
import Nav from "@/sections/Nav";
import Footer from "@/sections/Footer";
import { Toaster } from "@/components/ui/sonner";
import { JOURNEYS, CATEGORIES } from "@/lib/packages-data";
import { SITE_URL } from "@/lib/site-data";

const EASE = [0.76, 0, 0.24, 1];

export default function Journeys() {
  useLenis();
  useSEO({
    title: "Bhutan Tour Packages · All Private Journeys",
    description:
      "Browse every private Bhutan tour package from Planet Air Adventure in Paro — cultural circuits, Druk Path and Jomolhari treks, tsechu festivals, honeymoons and wellness retreats.",
    path: "/journeys",
  });
  const journeys = useContent("journeys", JOURNEYS);
  useJsonLd("journey-list", {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Bhutan Tour Packages by Planet Air Adventure",
    itemListElement: journeys.map((j, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: j.title,
      url: `${SITE_URL}/journeys/${j.slug}`,
    })),
  });
  const [filter, setFilter] = useState("All");
  const list =
    filter === "All" ? journeys : journeys.filter((j) => j.category === filter);

  return (
    <div className="App relative">
      <div className="grain" aria-hidden />
      <Nav />
      <main data-testid="journeys-page">
        {/* Header */}
        <section className="pt-36 md:pt-48 pb-14 md:pb-20 bg-background">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <div className="text-[11px] uppercase tracking-[0.3em] text-foreground/60 mb-6 flex items-center gap-3">
              <span className="w-6 h-px bg-primary" />
              <span>The Catalogue · {journeys.length} journeys</span>
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE }}
              className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.94] tracking-tighter max-w-4xl"
            >
              Every journey we <em className="italic text-primary">craft.</em>
            </motion.h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-foreground/70 font-light leading-relaxed">
              Every journey is fully private and endlessly editable. Open any
              itinerary, then tell us how to bend it.
            </p>

            {/* Filters */}
            <div className="mt-12 flex flex-wrap gap-2" data-testid="journey-filters">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`filter-${c.toLowerCase()}`}
                  className={`btn-sharp border px-5 py-2.5 text-[10px] uppercase tracking-[0.22em] font-medium transition-colors ${
                    filter === c
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-foreground/30 text-foreground/70 hover:border-foreground"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Grid */}
        <section className="pb-24 md:pb-36 bg-background">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <motion.div layout className="grid sm:grid-cols-2 gap-x-8 gap-y-16 md:gap-y-20">
              <AnimatePresence mode="popLayout">
                {list.map((j, i) => (
                  <motion.div
                    key={j.slug}
                    layout
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.7, ease: EASE, delay: i * 0.04 }}
                  >
                    <JourneyCard j={j} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

function JourneyCard({ j }) {
  return (
    <Link
      to={`/journeys/${j.slug}`}
      data-testid={`journey-card-${j.slug}`}
      className="group block"
    >
      <div className="relative overflow-hidden aspect-[4/3] bg-muted">
        <img
          src={j.img}
          alt={j.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover duotone transition-transform duration-[1200ms] ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-105"
        />
        <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.28em] text-white/90 mix-blend-difference">
          {j.number} · {j.category}
        </div>
      </div>
      <div className="mt-6 flex items-start justify-between gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-primary mb-2">
            {j.subtitle}
          </div>
          <h2 className="font-serif font-light text-3xl md:text-4xl leading-[0.98] tracking-tight group-hover:text-primary transition-colors">
            {j.title}
          </h2>
        </div>
        <span
          aria-hidden
          className="mt-2 shrink-0 text-xl transition-transform duration-500 group-hover:translate-x-2"
        >
          →
        </span>
      </div>
      <div className="mt-4 flex gap-8 border-t border-foreground/15 pt-4 text-sm">
        <div>
          <span className="text-[10px] uppercase tracking-[0.24em] text-foreground/55 block">
            Duration
          </span>
          <span className="font-serif text-lg">{j.duration}</span>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-[0.24em] text-foreground/55 block">
            From
          </span>
          <span className="font-serif text-lg">{j.from}</span>
        </div>
        <div className="hidden sm:block">
          <span className="text-[10px] uppercase tracking-[0.24em] text-foreground/55 block">
            Grade
          </span>
          <span className="font-serif text-lg">{j.difficulty.split("·")[0].trim()}</span>
        </div>
      </div>
    </Link>
  );
}
